import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { JsonForms } from '@jsonforms/react';
import { JsonFormsRendererRegistryEntry } from '@jsonforms/core';
import {
  materialRenderers,
  materialCells,
} from '@jsonforms/material-renderers';
import { Alert, CssBaseline } from '@mui/material';
import { ThemeProvider, createTheme, Theme } from '@mui/material/styles';
import CustomMaterialCategorizationStepperLayout, {
  CustomMaterialCategorizationStepperLayoutTester,
} from '../Layouts/CustomMaterialCategorizationStepperLayout';
import FileBase64Renderer, { 
  FileBase64RendererTester 
} from '../Layouts/FileBase64Renderer';
import { onBroadcast, sendBroadcast } from '../utils/broadcastUtils';
import { fetchFormByUuid, fetchThemeOptions } from '../utils/formsApi';
import { InstanceContext } from '../utils/InstanceContext';
import { ConfirmDialogProvider } from '../utils/ConfirmDialogContext';
import '../i18n';
import { diagnosticRenderer } from '../Layouts/DiagnosticsRenderer';
import { catchAllDebugRenderer } from '../Layouts/CatchAllRenderer';

// Custom renderers
const customRenderers: JsonFormsRendererRegistryEntry[] = [
  {
    tester: CustomMaterialCategorizationStepperLayoutTester,
    renderer: CustomMaterialCategorizationStepperLayout,
  },
  {
    tester: FileBase64RendererTester,
    renderer: FileBase64Renderer,
  },
];

// Combine custom renderers with material renderers, ensuring custom ones take precedence
const allRenderers = [...customRenderers, ...materialRenderers, catchAllDebugRenderer];

const FormPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const formUUID = searchParams.get('formUUID');
  const instanceUUID = searchParams.get('instanceUUID');
  const langParam = searchParams.get('lang');
  const themeParam = searchParams.get('theme');
  const [currentSchema, setCurrentSchema] = useState<any>(null);
  const [currentUiSchema, setCurrentUiSchema] = useState<any>(null);
  const [data, setData] = useState<any>({});
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentInstanceUUID, setCurrentInstanceUUID] = useState<string | null>(null);
  const [language, setLanguage] = useState<string>(langParam || 'en-US');
  const [themeName, setThemeName] = useState<string>('default');
  const [muiTheme, setMuiTheme] = useState<Theme>(createTheme({}));
  const { t, i18n } = useTranslation();

  const translate: any = useCallback((id: string, defaultMessage: string | undefined, values: any) => {
    // Check if the translation key exists in our i18next bundles.
    // We use i18n.exists because t() returns the key itself if missing.
    if (i18n.exists(id)) {
      return t(id, values);
    }
    
    // If not found and it's a validation error, return undefined.
    // This allows JSON Forms to try the next fallback key (e.g. falling back from email.error.required to error.required).
    if (values?.errors && values?.errors.length > 0) {
      return undefined;
    }

    if(!defaultMessage)
      console.log(`No translation or default message for ${language} ${id}`)

    return defaultMessage;
  }, [t, i18n]);

  const handleLoadContent = useCallback((payload: any) => {
    setCurrentSchema(payload.schema);
    setCurrentUiSchema(payload.uischema);
    setData(payload.data || {});
    if (payload.lang) setLanguage(payload.lang);
    if (payload.theme) setThemeName(payload.theme);
    setIsReadOnly(false);
    setErrorMessage(null);
  }, []);

  useEffect(() => {
    if (instanceUUID) {
      setCurrentInstanceUUID(instanceUUID);
    }
  }, [instanceUUID]);

  useEffect(() => {
    if (langParam) setLanguage(langParam);
    if (themeParam) setThemeName(themeParam);
  }, [langParam, themeParam]);

  useEffect(() => {
    if (i18n && typeof i18n.changeLanguage === 'function') {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  useEffect(() => {
    if (themeName && themeName !== 'default') {
      fetchThemeOptions(themeName)
        .then((themeOptions) => {
          const theme = createTheme(themeOptions);
          setMuiTheme(theme);
        })
        .catch((error) => {
          console.error('Failed to load theme:', error);
        });
    }
  }, [themeName]);

  useEffect(() => {
    if (formUUID) {
      fetchFormByUuid(formUUID)
        .then(({ jsonSchema, uiSchema, data: initialData }) => {
          handleLoadContent({
            schema: jsonSchema,
            uischema: uiSchema,
            data: initialData || {}
          });
        })
        .catch((error) => {
          console.error('Failed to fetch form by UUID:', error);
          setErrorMessage('Failed to load form configuration from server.');
        });
    }
  }, [formUUID, handleLoadContent]);

  useEffect(() => {
    if (currentSchema && currentUiSchema) {
      sendBroadcast('form-loaded', true);
    }
  }, [currentSchema, currentUiSchema]);

  useEffect(() => {
    const unsubscribe = onBroadcast((msg) => {
      console.log(`[FormPage Broadcast Listener] Action: ${msg.type}`, msg.payload);

      // Filter events based on instanceUUID to support multiple simultaneous instances
      const messageInstanceUUID = msg.payload?.instanceUUID || null;
      if (currentInstanceUUID && messageInstanceUUID !== currentInstanceUUID) {
        return;
      }

      if (msg.type === 'load-content') {
        handleLoadContent(msg.payload);
      }

      if (msg.type === 'form-submitted') {
        console.log('message form submitted received');
        setIsReadOnly(true);
      }

      if (msg.type === 'submit-error') {
        setIsReadOnly(false);
        setErrorMessage(msg.payload?.message || 'An error occurred during submission.');
      }
    });

    sendBroadcast('renderer-ready', true);

    return () => unsubscribe();
  }, [currentInstanceUUID, handleLoadContent]);

  console.log('Reloading FormPage');
  return (
    <InstanceContext.Provider value={currentInstanceUUID}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <ConfirmDialogProvider>
          <div>
            {currentSchema && currentUiSchema ? (
              <>
                <JsonForms
                  schema={currentSchema}
                  uischema={currentUiSchema}
                  data={data}
                  renderers={allRenderers}
                  cells={materialCells}
                  i18n={{ 
                    locale: language,
                    translate: translate
                  }}
                  config={{ 
                    reset: () => setData(null),
                    onSubmit: () => setIsReadOnly(true)
                  }}
                  onChange={({ data }) => !isReadOnly && setData(data)}
                  readonly={isReadOnly}
                />
                {errorMessage && (
                  <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mt: 2 }}>
                    {errorMessage}
                  </Alert>
                )}
              </>
            ) : (
              <div style={{ padding: '2em' }}>
                <p>Waiting for configuration content to be loaded ...</p>
              </div>
            )}
          </div>
        </ConfirmDialogProvider>
      </ThemeProvider>
    </InstanceContext.Provider>
  );
};

export default FormPage;
